// P0 · Entrar con el celular (decisión D51): el número y el código por SMS, o el código temporal que da el encargado
// (D16). Solo sirve si la empresa prendió `acceso.celular` (apagado por defecto); la pantalla de acceso muestra el
// enlace hasta acá únicamente si `GET /auth/opciones` dice `celular`. Lo normal es el correo y la contraseña.
import { normalizarTelefono } from '@reparto/shared/nucleo';
import { Boton, Campo, useToast } from '@reparto/ui';
import { type FormEvent, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { AvisoDemostracion, EnlacePie, type EstadoAcceso, MarcoAcceso } from '../componentes/MarcoAcceso';
import { ErrorApi, SinRed } from '../datos/api';
import { entrarConCodigoAcceso, entrarConOtp, solicitarCodigo } from '../datos/sesion';

type Modo = 'telefono' | 'codigo_otp' | 'codigo_deposito';

export function AccesoCelular() {
  const navegar = useNavigate();
  const estado = useLocation().state as EstadoAcceso | null;
  const { mostrar } = useToast();
  const [modo, setModo] = useState<Modo>('telefono');
  const [telefono, setTelefono] = useState('');
  const [codigo, setCodigo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ayudaDesarrollo, setAyudaDesarrollo] = useState<string | null>(null);

  const fallar = (e: unknown) => {
    if (e instanceof SinRed) setError('No hay conexión. Para entrar la primera vez necesitás señal.');
    else if (e instanceof ErrorApi) setError(e.message);
    else setError('No se pudo entrar. Probá de nuevo.');
  };

  async function pedirCodigo(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    if (!normalizarTelefono(telefono)) return setError('Revisá el número: por ejemplo 0981 234 567');
    setOcupado(true);
    try {
      const r = await solicitarCodigo(telefono);
      setAyudaDesarrollo(r.codigo_desarrollo ?? null);
      setModo('codigo_otp');
    } catch (e) {
      fallar(e);
    } finally {
      setOcupado(false);
    }
  }

  async function entrar(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(codigo)) return setError('El código tiene 6 números');
    setOcupado(true);
    try {
      const s = modo === 'codigo_otp' ? await entrarConOtp(telefono, codigo) : await entrarConCodigoAcceso(codigo);
      mostrar(`Hola, ${s.usuario.nombre.split(' ')[0]}`);
      navegar('/', { replace: true });
    } catch (e) {
      fallar(e);
    } finally {
      setOcupado(false);
    }
  }

  return (
    <MarcoAcceso>
      {modo === 'telefono' ? (
        <form onSubmit={pedirCodigo} className="space-y-4">
          <h2 className="text-xl font-medium">Ingresá con tu celular</h2>
          <Campo
            etiqueta="Número de celular"
            icono="smartphone"
            inputMode="tel"
            autoComplete="tel"
            placeholder="0981 234 567"
            value={telefono}
            onChange={(e) => setTelefono(e.target.value)}
            error={error}
          />
          <Boton type="submit" bloque tamano="lg" icono="sms" cargando={ocupado}>
            Recibir código
          </Boton>
          <Boton variante="fantasma" bloque onClick={() => (setModo('codigo_deposito'), setError(null), setCodigo(''))}>
            Tengo un código del depósito
          </Boton>
        </form>
      ) : (
        <form onSubmit={entrar} className="space-y-4">
          <h2 className="text-xl font-medium">{modo === 'codigo_otp' ? 'Escribí el código' : 'Código del depósito'}</h2>
          <p className="text-sm text-stone-600 dark:text-stone-300">
            {modo === 'codigo_otp'
              ? `Te lo mandamos al ${telefono}.`
              : 'Pedíselo al encargado: vale 10 minutos y sirve una sola vez.'}
          </p>
          {ayudaDesarrollo ? (
            <AvisoDemostracion>
              Demostración con datos simulados: tu código es <b className="tabular-nums">{ayudaDesarrollo}</b>. Con
              datos reales llega por SMS o te lo da el encargado.
            </AvisoDemostracion>
          ) : null}
          <Campo
            etiqueta="Código de 6 números"
            icono="pin"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={codigo}
            onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
            error={error}
            className="[&_input]:text-center [&_input]:text-2xl [&_input]:tracking-[0.4em]"
          />
          <Boton type="submit" bloque tamano="lg" icono="login" cargando={ocupado}>
            Entrar
          </Boton>
          <Boton
            variante="fantasma"
            bloque
            onClick={() => (setModo('telefono'), setError(null), setCodigo(''), setAyudaDesarrollo(null))}
          >
            Volver
          </Boton>
        </form>
      )}
      <div className="mt-6 border-t border-stone-200 pt-4 dark:border-stone-800">
        <EnlacePie a="/acceso" icono="mail" estado={estado ?? undefined}>
          Entrar con el correo y la contraseña
        </EnlacePie>
      </div>
    </MarcoAcceso>
  );
}
